import { CSSProperties, useCallback, useContext, useMemo, useState, VFC } from 'react'
import {
  Button,
  Body,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader, LoadingIcon, Bold, ModalProps,
} from '@panwds/react-ui'
import {
  BUTTON_REMOVE,
  BUTTON_CANCEL,
  ERROR_SERVER_REMOVE,
  LABEL_REMOVE_ROLE,
  LABEL_ALL_APPS_SERVICES,
  LABEL_REMOVE_LAST_ROLE
} from '../../../constants/LabelConstants'
import {
  DATA_TEST_REMOVE_IDENTITY_SUBMIT,
  DATA_TEST_REMOVE_IDENTITY_CANCEL
} from '../../../../common/constants/TestingDataIds'

import '../../../styles/Modals.scss'
import AppContext from '../../../../common/context/AppContext'
import GlobalContext from '../../../../common/context/GlobalContext'
import IAMService from '../../../services/IAMService'
import IAMAppName from '../../../utils/IAMAppName'
import StringTemplate from '../../../../common/utils/StringTemplate'

const RemoveIdentityModal: VFC<ModalProps & {
  closeModal: () => void,
  policy: any,
  user: any,
  getData: Function,
  removeIdentity: Function
}> = ({
  isOpen,
  closeModal,
  policy,
  user,
  getData,
  removeIdentity
}) => {

  const { getAppName } = useContext(AppContext)
  const { iamReducer, treeReducer } = useContext(GlobalContext)
  const { roleMap, customRoleMap, accessPolicies } = iamReducer
  const { selectedTreeNode } = treeReducer

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const role = policy?.role
  const roleName = customRoleMap[role]?.label || customRoleMap[role]?.name || roleMap[role]?.label || role
  const appName = policy?.resource?.app_id ? IAMAppName(policy?.resource?.app_id, getAppName) : LABEL_ALL_APPS_SERVICES
  const displayName = user?.display_name.trim() || user?.identity_email

  const onRemove = useCallback(async () => {
    setIsLoading(true)
    setError('')
    try {
      if (user?.policies?.length === 1) {
        await removeIdentity(selectedTreeNode?.id, [user])
      }
      else {
        await IAMService.accessPolicies.delete(selectedTreeNode?.id, policy?.id)
      }
      getData()
      closeModal()
    }
    catch (error: any) {
      setError(error.message || ERROR_SERVER_REMOVE)
    }
    setIsLoading(false)
  }, [closeModal, getData, policy?.id, removeIdentity, selectedTreeNode?.id, user])

  const numPolicies = useMemo(() => {
    return accessPolicies?.filter(({ principal }) => policy.principal === principal).length
  }, [accessPolicies, policy?.principal])

  return (
    <Modal style={{ top: '50%', '--tw-translate-y': '-50%' } as CSSProperties} size='sm' onClose={closeModal} isOpen={isOpen}>
      <ModalHeader title={LABEL_REMOVE_ROLE}/>
      {isLoading ? (
        <ModalBody addClassName='tw-flex tw-flex-row tw-justify-center tw-items-center'>
          <LoadingIcon size="5xl"/>
        </ModalBody>
      ) : (
        <ModalBody addClassName='tw-overflow-y-scroll'>
          {error && <Bold appearance='semibold' addClassName='tw-text-error'>{error}</Bold>}
          <div className='tw-flex tw-flex-col tw-gap-2'>
            <Body>You are about to remove {`${displayName}${displayName.endsWith('s') ? '\'' : '\'s'}`} <Bold>{roleName}</Bold> role from <Bold>{appName}</Bold></Body>
            {numPolicies === 1 && (
              <Body>{StringTemplate(LABEL_REMOVE_LAST_ROLE, [policy?.principal, selectedTreeNode?.display_name])}</Body>
            )}
          </div>
        </ModalBody>
      )}
      <ModalFooter>
        <Button
          data-testid={DATA_TEST_REMOVE_IDENTITY_CANCEL}
          onClick={closeModal}
          appearance="secondary"
          disabled={isLoading}
        >{BUTTON_CANCEL}</Button>
        <ModalCloseButton
          data-testid={DATA_TEST_REMOVE_IDENTITY_SUBMIT}
          onClick={onRemove.bind(this)}
          appearance='secondary-destructive'
          disabled={Boolean(isLoading || error)}
        >{BUTTON_REMOVE}</ModalCloseButton>
      </ModalFooter>
    </Modal>
  )
}

export default RemoveIdentityModal
